import React, { ReactNode, useEffect, useMemo, useState } from "react"
import SubsectionContainer from "../shared/SubsectionContainer"
import { useSlideOver } from "../../useSlideOver"
import { useAppDispatch } from "../../../reduxStore/hooks"
import {
  dataStatusesLoading,
  formatPhoneNumber,
  sortProfilePhotos,
  getAggregateInsuranceStatus,
  LiabilityStatusEnum,
} from "../../shared/utils"
import { getLocationLiabilityStatus } from "../utils/liabilityStatusHelpers"
import {
  fetchLocationFullPhoto,
  fetchLocationPhotoThumbnail,
} from "../../../domain/Profile/model"
import { ensurePhotoDataUrl } from "../../../domain/Profile/searchResultMapper"
import { DataStatus } from "../../../domain/enums"
import { useSelector } from "react-redux"
import { useCMSReady } from "../../../domain/CMS/useCMSReady"
import { PublicLocation, PublicPractice } from "../../../domain/Profile/schema"
import { RequestReferralModal } from "../../shared/components/RequestReferral/RequestReferralModal"
import StaticMapThumbnail from "../../shared/components/StaticMapThumbnail"
import { getCombinedAddress } from "../practice/Locations/helpers"
import PlaceHolder from "../../../assets/PlaceholderImage.png"
import {
  selectCurrentUserRoles,
  UserRoles,
} from "../../../domain/Account/userSlice"
import { LiabilityStatusBadge } from "../shared/LiabilityStatusBadge"
import {
  LiabilityInsurance,
  ProviderDisclosure,
} from "../../../domain/Provider/schema"
import { ProviderTypeDisplayId } from "../../../domain/enums/displayId.enum"
import Link from "../../shared/components/Link"
import Thumbnail from "../../shared/components/Thumbnail"
import ColumnContainer from "../../shared/components/Layout/ColumnContainer"
import RowContainer from "../../shared/components/Layout/RowContainer"
import { ButtonWrapper } from "../../shared/components/ButtonWrapper"
import { Typography, typographyTypes } from "../../shared/components/Typography"
import Button from "../../shared/components/Button"
import { LocationTypeDisplayId } from "../../../domain/Practice/schema"

export type LocationPhoto = {
  id: string
  name?: string
  fileName: string
  imageUrl: string
  description?: string
  isLoading?: boolean
}
type LocationPhotoRecord = Record<string, LocationPhoto>

interface LocationsContentProps {
  activePublicPractices: PublicPractice[]
  isUnderSupervision: boolean
  providerId: string
  providerName: string
  providerStatusDisplayId?: string
  providerLiabilityInsurances?: LiabilityInsurance[]
  hasLicense: boolean
  providerDisclosures?: ProviderDisclosure[]
  providerTypeDisplayId?: string
  isFromSearch?: boolean
}

export const LocationsContent: React.FC<LocationsContentProps> = ({
  activePublicPractices,
  isUnderSupervision,
  providerId,
  providerName,
  providerStatusDisplayId,
  providerLiabilityInsurances,
  hasLicense,
  providerDisclosures,
  providerTypeDisplayId,
  isFromSearch = false,
}) => {
  const { cms: cmsContent } = useCMSReady(["common", "publicProfileSlide"])
  const commonText = cmsContent.common
  const publicProfileText = cmsContent.publicProfileSlide

  const dispatch = useAppDispatch()
  const userRoles = useSelector(selectCurrentUserRoles)
  const {
    handleSetShowProfileModal,
    showProfileSidebar,
    handleSetShowProfileSidebar,
  } = useSlideOver()
  const canRequestReferral = [
    UserRoles.ROLE_AFFILIATE_RELATIONS,
    UserRoles.ROLE_EAP_RESEARCH,
  ].some((role) => userRoles.includes(role))
  const [locationThumbnail, setLocationThumbnail] =
    useState<LocationPhotoRecord>({})
  const [locationFullImages, setLocationFullImages] = useState<LocationPhoto[]>(
    [],
  )
  const [thumbnailDataStatus, setThumbnailDataStatus] = useState<DataStatus>(
    DataStatus.LOADING,
  )
  const [fullImageDataStatus, setFullImageDataStatus] = useState<DataStatus>(
    DataStatus.LOADING,
  )
  const [carouselLocationId, setCarouselLocationId] = useState<string>()

  const [selectedPractice, setSelectedPractice] =
    useState<PublicPractice | null>(null)
  const [selectedLocation, setSelectedLocation] =
    useState<PublicLocation | null>(null)

  const [isRequestReferralModalOpen, setIsRequestReferralModalOpen] =
    useState(false)

  const thumbnailsLoading = dataStatusesLoading([thumbnailDataStatus])
  const imageCarouselText = useMemo(
    () => ({
      imageAriaLabel: commonText?.thumbnail || "",
      previousButton: commonText?.previous || "",
      nextButton: commonText?.next || "",
    }),
    [commonText?.next, commonText?.previous, commonText?.thumbnail],
  )

  const panelStyleOverrides =
    "min-w-[862px] min-h-[664px] p-3 sm:p-3 pb-16 sm:pb-16"
  const titleStyleOverrides = "text-[18px] font-medium ml-5"

  const handleOnPracticeClick = (practiceId: string) => {
    if (showProfileSidebar.practiceId !== practiceId) {
      dispatch(() => {
        handleSetShowProfileSidebar({ practiceId })
      })
    }
  }

  useEffect(() => {
    const fetchPhotoThumbnailUrl = async () => {
      if (thumbnailDataStatus !== DataStatus.LOADING) {
        return
      }
      const locationPhotoRecord: LocationPhotoRecord = {}
      for (const practice of activePublicPractices) {
        for (const location of practice.locations) {
          if (location.locationPhotos.length > 0) {
            // Search-driven path: the index attaches base64 to exactly one
            // photo per location, and that one is the intended default.
            const inlineThumbnail = location.locationPhotos.find(
              (p) => p.thumbnailBase64,
            )
            if (inlineThumbnail) {
              locationPhotoRecord[location.id] = {
                id: inlineThumbnail.id,
                name: inlineThumbnail.name,
                fileName: inlineThumbnail.fileName,
                imageUrl: ensurePhotoDataUrl(inlineThumbnail.thumbnailBase64!),
                description: inlineThumbnail.description,
              }
              continue
            }

            // Search-driven path: the practice thumbnail endpoint is not
            // available to public-view callers — skip the fetch and let the
            // photo render without a thumbnail.
            if (isFromSearch) continue

            const photos = location.locationPhotos.toSorted(sortProfilePhotos)
            const defaultThumbnail = photos[0]

            // DB-fetched fallback path (non-search entry points).
            const encodedLocationPhotoString =
              await fetchLocationPhotoThumbnail(
                practice.id,
                defaultThumbnail.id,
                setThumbnailDataStatus,
              )
            // we could handle null return here indicating an error
            // we dont want to fail all images if one call fails so for now, im just discarding on failure
            if (encodedLocationPhotoString) {
              const locationPhotoRecordValue: LocationPhoto = {
                id: defaultThumbnail.id,
                name: defaultThumbnail.name,
                fileName: defaultThumbnail.fileName,
                imageUrl: encodedLocationPhotoString,
                description: defaultThumbnail.description,
              }
              locationPhotoRecord[location.id] = locationPhotoRecordValue
            }
          }
        }
      }
      setLocationThumbnail(locationPhotoRecord)
      setThumbnailDataStatus(DataStatus.LOADED)
    }

    fetchPhotoThumbnailUrl()
  }, [activePublicPractices, thumbnailDataStatus, isFromSearch])

  useEffect(() => {
    const fetchLocationFullImages = async (locationId: string) => {
      const activeLocations = activePublicPractices.flatMap(
        (practice) => practice.locations,
      )
      const selectedLocation = activeLocations.find(
        (location) => location.id === locationId,
      )
      const selectedLocationPhotoIds =
        selectedLocation?.locationPhotos.map((photo) => photo.id) ?? []
      const locationFullPhotos: LocationPhoto[] = []
      for (const photoId of selectedLocationPhotoIds) {
        const encodedLocationPhotoString =
          (await fetchLocationFullPhoto(
            locationId,
            photoId,
            setFullImageDataStatus,
          )) ?? PlaceHolder
        const selectedLocationPhoto = selectedLocation?.locationPhotos.find(
          (photo) => photo.id === photoId,
        )
        // we dont want to fail all images if one call fails so for now, im just discarding on failure
        if (!!selectedLocationPhoto) {
          const locationPhotoRecordValue: LocationPhoto = {
            id: photoId,
            name: selectedLocationPhoto.name,
            fileName: selectedLocationPhoto.fileName,
            imageUrl: encodedLocationPhotoString,
            description: selectedLocationPhoto.description,
            isLoading: false,
          }
          locationFullPhotos.push(locationPhotoRecordValue)
        }
      }
      setLocationFullImages(locationFullPhotos)
      handleSetShowProfileModal({
        modalComponentRegistryKey: {
          type: "imageCarousel",
          onCancel: false,
          onSubmit: false,
          props: {
            locationPhotos: locationFullPhotos,
            text: imageCarouselText,
          },
        },
        panelStyleOverrides,
        titleStyleOverrides,
      })
      // reset state so it is primed for next carousel click
      setCarouselLocationId(undefined)
      setFullImageDataStatus(DataStatus.LOADING)
    }

    if (carouselLocationId && fullImageDataStatus === DataStatus.LOADING) {
      fetchLocationFullImages(carouselLocationId)
    }
  }, [
    activePublicPractices,
    carouselLocationId,
    fullImageDataStatus,
    handleSetShowProfileModal,
    imageCarouselText,
    locationFullImages,
  ])

  const onMultiThumbnailClick = (location: PublicLocation) => {
    // fetch the full images using the location.photoIds
    const tempLocationPhotos: LocationPhoto[] = location.locationPhotos.map(
      (photo) => ({
        id: photo.id,
        name: photo.name,
        fileName: photo.fileName,
        imageUrl: "placeholder",
        description: photo.description,
        isLoading: true,
      }),
    )

    const tempCarouselProps = {
      locationPhotos: tempLocationPhotos,
      text: imageCarouselText,
    }
    handleSetShowProfileModal({
      modalComponentRegistryKey: {
        type: "imageCarousel",
        props: tempCarouselProps,
      },
      panelStyleOverrides,
      titleStyleOverrides,
    })
    setCarouselLocationId(location.id)
  }

  const getThumbnailComponent = (
    location: PublicLocation,
  ): ReactNode | undefined => {
    const thumbnail = locationThumbnail[location.id] ?? {
      id: "",
      fileName: "",
      name: "",
      imageUrl: "",
    }
    const numberOfPhotos = location.locationPhotos.length
    const isMultiplePhotos = numberOfPhotos > 1
    const bubbleIndicatorText = `+${numberOfPhotos - 1}`
    const defaultPhotoName = thumbnail.name || thumbnail.fileName

    let thumbnailComponent
    if (numberOfPhotos >= 1) {
      thumbnailComponent = (
        <Thumbnail
          id={location.id}
          imageUrl={thumbnail.imageUrl}
          imageAltText={defaultPhotoName}
          ariaLabel={`${imageCarouselText.imageAriaLabel} - ${defaultPhotoName}`}
          isLoading={thumbnailsLoading}
          onClick={() => onMultiThumbnailClick(location)}
          bubbleIndicator={
            isMultiplePhotos ? { text: bubbleIndicatorText } : undefined
          }
          styleOverrides={`w-[132px] h-[100px] ${isMultiplePhotos && !thumbnailsLoading ? "cursor-pointer" : ""} `}
        />
      )
    }

    return thumbnailComponent
  }

  return (
    <ColumnContainer>
      {activePublicPractices.map((practice) =>
        practice.locations.map((location) => (
          <SubsectionContainer
            key={`location-content-${practice.id}-${location.city}-${location.line1}-${location.line2}`}
          >
            <RowContainer styleOverrides="w-full flex justify-between">
              <ColumnContainer styleOverrides="space-y-6 pt-2">
                <ColumnContainer styleOverrides="space-y-0">
                  <LiabilityStatusBadge
                    practice={practice}
                    providerLiabilityInsurances={providerLiabilityInsurances}
                  />
                  <ButtonWrapper
                    onClick={() => handleOnPracticeClick(practice.id)}
                    styleOverrides="p-0"
                  >
                    <Typography
                      text={practice.name}
                      variant={typographyTypes.NORMAL}
                      styleOverrides="text-link-blue underline"
                    />
                  </ButtonWrapper>
                  {location.locationTypeDisplayId !==
                  LocationTypeDisplayId.VIRTUAL ? (
                    <div>
                      <Typography
                        text={`${location.line1}${location.line2 ? `, ${location.line2}` : ""}`}
                        variant={typographyTypes.NORMAL}
                      />
                      <Typography
                        text={`${location.city}${location.stateOrTerritory ? `, ${location.stateOrTerritory}` : ""}${location.postalCode ? `, ${location.postalCode}` : ""}`}
                        variant={typographyTypes.NORMAL}
                      />
                      {practice.websiteUrl && (
                        <Link
                          text={practice.websiteUrl}
                          destination={
                            practice.websiteUrl.startsWith("http")
                              ? practice.websiteUrl
                              : `https://${practice.websiteUrl}`
                          }
                          underlined
                          // TODO: fix - not sure why we need -2px here but we do for even spacing
                          styleOverrides="text-sm text-link-blue !mt-[-4px]"
                        />
                      )}
                      {location.phones.map((phone) => (
                        <Typography
                          key={phone.number}
                          text={formatPhoneNumber(phone.number, true)}
                          variant={typographyTypes.NORMAL}
                        />
                      ))}
                    </div>
                  ) : (
                    <div>
                      <Typography
                        text={publicProfileText?.locationsOnlinePractice}
                        variant={typographyTypes.NORMAL}
                      />
                      {location.phones.map((phone) => (
                        <Typography
                          key={phone.number}
                          text={formatPhoneNumber(phone.number, true)}
                          variant={typographyTypes.NORMAL}
                        />
                      ))}
                    </div>
                  )}
                </ColumnContainer>
                {canRequestReferral &&
                  practice.status === "active" &&
                  providerStatusDisplayId === "active" && (
                    <Button
                      buttonLabel={publicProfileText?.requestReferral}
                      type="transparent"
                      roundedFull={false}
                      typographyStyleOverrides="font-semibold text-gray-700"
                      styleOverrides="rounded-lg"
                      onClick={() => {
                        setSelectedPractice(practice)
                        setSelectedLocation(location)
                        setIsRequestReferralModalOpen(true)
                      }}
                    />
                  )}
              </ColumnContainer>
              {location.locationTypeDisplayId !==
                LocationTypeDisplayId.VIRTUAL && (
                <RowContainer>
                  <StaticMapThumbnail
                    marker={{
                      id: location.id,
                      address: getCombinedAddress(
                        location,
                        commonText?.virtual,
                      ),
                    }}
                  />
                  {getThumbnailComponent(location) &&
                    getThumbnailComponent(location)}
                </RowContainer>
              )}
            </RowContainer>
          </SubsectionContainer>
        )),
      )}
      {isRequestReferralModalOpen && selectedPractice && selectedLocation && (
        <RequestReferralModal
          isOpen={isRequestReferralModalOpen}
          onClose={() => setIsRequestReferralModalOpen(false)}
          providerId={providerId}
          practiceId={selectedPractice.id}
          isUnderSupervision={isUnderSupervision}
          locationData={{
            locationId: selectedLocation.id,
            provider: providerName,
            practice: selectedPractice.name,
            careType:
              selectedLocation.locationTypeDisplayId ===
              LocationTypeDisplayId.VIRTUAL
                ? (commonText?.virtual ?? "")
                : (commonText?.inPerson ?? ""),
            officeLocation:
              selectedLocation.locationTypeDisplayId ===
              LocationTypeDisplayId.VIRTUAL
                ? (commonText?.virtual ?? "")
                : `${selectedLocation.line1}${selectedLocation.line2 ? " " + selectedLocation.line2 : ""}, ${selectedLocation.city}, ${selectedLocation.stateOrTerritory} ${selectedLocation.postalCode}`,
          }}
          hasPracticeLiabilityInsurance={
            getLocationLiabilityStatus(
              selectedPractice,
              providerLiabilityInsurances,
            ).hasActiveLiability
          }
          hasProviderLiabilityInsurance={[
            LiabilityStatusEnum.ACTIVE,
            LiabilityStatusEnum.EXPIRING_SOON,
          ].includes(
            getAggregateInsuranceStatus(providerLiabilityInsurances || []),
          )}
          hasProviderLicense={hasLicense}
          hasProviderDisclosure={
            (providerDisclosures || []).some(
              (disclosure) => disclosure.fileId && disclosure.envelopeId,
            ) || providerTypeDisplayId === ProviderTypeDisplayId.MANAGED
          }
        />
      )}
    </ColumnContainer>
  )
}
